import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ViewSuggestion } from './useAgentChat';

/**
 * Shared navigator for agent-emitted view suggestions. Used by the drawer,
 * the full-page program agent, and AgentHome so the switch stays in one place.
 *
 * Returns whether it navigated (false for an unknown view or missing params),
 * so callers can follow up — e.g. AgentHome opens the drawer to carry the
 * conversation onto the page it just navigated to.
 */
export const useNavigateToView = () => {
  const navigate = useNavigate();

  return useCallback(
    (suggestion: ViewSuggestion): boolean => {
      const { view, params = {} } = suggestion;
      console.log('🧭 Navigating to view:', view, 'with params:', params);

      const go = (path: string): boolean => {
        navigate(path);
        return true;
      };

      switch (view) {
        case 'workflow_step':
          if (params.programId && params.stepKey) {
            return go(`/programs/${params.programId}/module/${encodeURIComponent(params.stepKey)}`);
          }
          if (params.programId) {
            return go(`/programs/${params.programId}`);
          }
          break;
        case 'workflow_graph':
          if (params.programId) {
            return go(`/programs/${params.programId}`);
          }
          break;
        case 'scorecard':
        case 'scorecard_edit':
          if (params.programId) {
            return go(`/programs/${params.programId}/setup/scorecards`);
          }
          break;
        case 'vendor_detail':
          if (params.programId && params.vendorId) {
            return go(`/programs/${params.programId}/vendors/${params.vendorId}`);
          }
          break;
        case 'vendor_comparison':
          if (params.programId) {
            return go(`/programs/${params.programId}/vendors`);
          }
          break;
        case 'program_list':
          return go('/programs');
        case 'program':
          if (params.programId) {
            return go(`/programs/${params.programId}`);
          }
          break;
        case 'program_market':
          if (params.programId) {
            return go(`/programs/${params.programId}/market`);
          }
          break;
        case 'vendor':
          return go(params.programId ? `/programs/${params.programId}/vendors` : '/market');
        case 'marketview':
          return go(params.programId ? `/programs/${params.programId}/market` : '/market');
        case 'document':
          if (params.programId && params.documentId) {
            return go(`/programs/${params.programId}/documents/${params.documentId}`);
          }
          break;
        default:
          console.warn('Unknown view type:', view);
      }
      return false;
    },
    [navigate]
  );
};
